import { PayloadAction } from '@reduxjs/toolkit';

import { PatientCareTeamMemberDto, PatientCareTeamMemberSaveRequest, PatientCareTeamMembersClient } from '@medone/medonehp-api-client';

import { handleError } from '../../../../shared/common/HandleErrors';
import { shouldSkipByPatientId } from '../../../../shared/common/helpers';
import { Axios } from '../../../../shared/common/http';
import { AppDispatch, AppThunk, RootState } from '../../../../shared/store';
import { CensusState } from './models';
import { censusSlice } from './slice';

// A save either succeeds (list refetched) or comes back with a readable message for the modal to show inline.
// A stale rowVersion arrives as a succeeded:false envelope, but the save gate's own rules are thrown: the
// server raises MedOneValidationException, which the API maps to a 400 ProblemDetails, so the catch has to
// read them out. (SCRUM-1117 rationale, transferred to SCRUM-1215.)
export type SaveCareTeamMemberResult = { succeeded: boolean; message?: string };

// SCRUM-1215 - the stale-patient guard reads census.currentCensus, which only the chart maintains. Census-less
// routes (the Clinical Coordinator Medical Summary) can still have it pinned to a patient edited earlier in the
// shift, so their members would be discarded as another patient's; they opt out. The ownership tag on the list is
// what keeps that safe: the panel renders rows only while they are tagged to the patient it is showing.
type CareTeamMemberGuardOptions = { bypassActivePatientGuard?: boolean };

const shouldDiscard = (state: RootState, patientId: number, options?: CareTeamMemberGuardOptions) => !options?.bypassActivePatientGuard && shouldSkipByPatientId(state.census, patientId);

export const reducers = {
    setPatientCareTeamMembersLoading: (state: CensusState, action: PayloadAction<boolean>) => {
        state.patientCareTeamMembersLoading = action.payload;
    },

    // The list and the patient it belongs to are written together so they can never disagree: a caller cannot
    // store rows and forget to say whose they are.
    setPatientCareTeamMembers: (state: CensusState, action: PayloadAction<{ patientId: number; members: PatientCareTeamMemberDto[] }>) => {
        state.patientCareTeamMembers = action.payload.members;
        state.patientCareTeamMembersPatientId = action.payload.patientId;
    },
};

// SCRUM-1215: care team members live in the census slice (not a panel-local hook) because the Medical Summary
// form renders the panel, and a second copy of the list could disagree after a write.
export function fetchPatientCareTeamMembers(patientId: number, options?: CareTeamMemberGuardOptions): AppThunk {
    return async (dispatch: AppDispatch, getState: () => RootState) => {
        dispatch(censusSlice.actions.setPatientCareTeamMembersLoading(true));
        // SCRUM-1117 - both mount points read this one list, so ANOTHER patient's rows have to go before the new
        // fetch resolves; the loading mask is translucent and they would stay readable underneath it. Rows that
        // already belong to this patient stay put: every save and delete awaits a refetch, and blanking those
        // flashes "no care team members documented" on the chart, the same misread on the common path.
        if (getState().census.patientCareTeamMembersPatientId !== patientId) {
            dispatch(censusSlice.actions.setPatientCareTeamMembers({ patientId, members: [] }));
        }

        const client = new PatientCareTeamMembersClient(null, Axios);

        try {
            const response = await client.getByPatient(patientId);

            // Guard against race condition: discard if user navigated to a different patient.
            if (shouldDiscard(getState(), patientId, options)) {
                return;
            }

            if (response.result.succeeded) {
                dispatch(censusSlice.actions.setPatientCareTeamMembers({ patientId, members: response.result.entity ?? [] }));
            } else {
                handleError(response, () => dispatch(censusSlice.actions.setError(null)));
            }
        } catch (error) {
            handleError(error, () => dispatch(censusSlice.actions.setError(error.toString())));
        } finally {
            // In a finally, not after the try: the race guard returns early, and a discarded fetch that never
            // cleared the flag would leave the table spinning for good.
            dispatch(censusSlice.actions.setPatientCareTeamMembersLoading(false));
        }
    };
}

// memberId null = create, otherwise update. Refetches on success so the server-resolved state lands.
export function savePatientCareTeamMember(
    patientId: number,
    memberId: number | null,
    request: PatientCareTeamMemberSaveRequest,
    options?: CareTeamMemberGuardOptions
): AppThunk<Promise<SaveCareTeamMemberResult>> {
    return async (dispatch: AppDispatch, getState: () => RootState) => {
        const client = new PatientCareTeamMembersClient(null, Axios);

        try {
            const response = memberId != null ? await client.update(patientId, memberId, request) : await client.create(patientId, request);

            if (response.result.succeeded) {
                // Skip the refetch if the user has since navigated to another patient.
                if (!shouldDiscard(getState(), patientId, options)) {
                    await dispatch(fetchPatientCareTeamMembers(patientId, options));
                }

                return { succeeded: true };
            }

            return { succeeded: false, message: response.result.message };
        } catch (error) {
            // Only a 400 carries the save gate's own wording; a 500's detail is server internals, not a rule a
            // clinician can act on, so it stays behind the generic message.
            const validationDetail = error?.status === 400 ? error?.response?.detail : null;

            // The modal surfaces this inline; don't also toast via handleError, or a thrown save (e.g. a dropped
            // connection) would report the same failure twice.
            return { succeeded: false, message: validationDetail || 'Something went wrong saving the care team member.' };
        }
    };
}

// Removal has no inline surface (the row's Popconfirm closes either way), so a failure toasts.
export function removePatientCareTeamMember(patientId: number, memberId: number, options?: CareTeamMemberGuardOptions): AppThunk {
    return async (dispatch: AppDispatch, getState: () => RootState) => {
        const client = new PatientCareTeamMembersClient(null, Axios);

        try {
            const response = await client.deleteMember(patientId, memberId);

            if (response.result.succeeded) {
                if (!shouldDiscard(getState(), patientId, options)) {
                    await dispatch(fetchPatientCareTeamMembers(patientId, options));
                }
            } else {
                handleError(response, () => dispatch(censusSlice.actions.setError(null)));
            }
        } catch (error) {
            handleError(error, () => dispatch(censusSlice.actions.setError(error.toString())));
        }
    };
}

// One shared instance, never an inline []: a fresh array on every render would break the selector's referential
// check and re-render the panel on every unrelated census change.
const NO_MEMBERS: PatientCareTeamMemberDto[] = [];

// SCRUM-1215 - the list and its ownership tag are read together, so there is no ungated way to render the rows.
// The fetch that replaces them is dispatched from an effect, which React commits AFTER the render, so reading
// the list alone paints the previous patient's members under the new patient's header.
export const selectPatientCareTeamMembersFor = (patientId?: number) => (state: RootState) =>
    state.census.patientCareTeamMembersPatientId === patientId ? state.census.patientCareTeamMembers : NO_MEMBERS;

export const selectPatientCareTeamMembersLoading = (state: RootState) => state.census.patientCareTeamMembersLoading;
